import { useEffect, useMemo, useRef, useState } from 'react';
import { useDefaultPeriod, useOrgFilter } from '@/store/defaults';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ColDef, GridApi } from 'ag-grid-community';
import { bankPayments, type BankPaymentListItem } from '@/api/bankDocs';
import { bankApi } from '@/api/bankApi';
import { contractors as contractorsApi, orgs } from '@/api/catalogs';
import { refs } from '@/api/refs';
import { taxInvoicesIncoming } from '@/api/taxInvoices';
import DatePopover from '@/components/DatePopover';
import BankImportPage from '@/pages/BankImportPage';
import SearchSelect from '@/components/SearchSelect';
import InfiniteDocGrid from '@/shared/components/InfiniteDocGrid';
import SelectionSum from '@/shared/components/SelectionSum';
import { sumMoney } from '@/shared/utils/selectionSum';
import ImportedBadge from '@/shared/components/ImportedBadge';
import { useInfiniteGridDatasource } from '@/hooks/useInfiniteGridDatasource';
import { useBankApiRefreshOnOpen } from '@/hooks/useBankApiRefresh';
import { useSignControlInfinite } from '@/shared/hooks/useSignControl';
import { everVatPayer } from '@/shared/utils/vatFlags';

const fmt = (v: string | number) =>
  Number(v).toLocaleString('uk-UA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// ч.727 (рішення власника): дві вкладки — «сплачені» (проведені, факт за
// випискою) і «збережені» (чернетки, підготовлені до відправки у банк:
// статус чернетка/відправлено, мультивибір → реєстр csv у клієнт-банк).
// ч.734: серверна пагінація/сортування (InfiniteDocGrid, Закон дизайну #1) —
// ГАЛІС має тисячі списань; підсумок (кількість+сума) рахує сервер.
// ч.833 (рішення власника): «нерозпізнані» (з виписки, needs_review — та сама таблиця, що
// після розбору виписки) / «збережені» (чернетки) / «відправлені» (файл або API); лічильники.
type Tab = 'paid' | 'review' | 'saved' | 'sent';
const TABS: Tab[] = ['paid', 'review', 'saved', 'sent'];

const BANK_HINT: Record<string, string> = {
  privat_csv: 'Приват24 для бізнесу → Платежі → Імпорт → файл формату НБУ (csv)',
  pumb_csv: 'ПУМБ Online → Платежі → Імпорт платежів → csv',
  '1c': 'Клієнт-банк → Імпорт з 1С (1CClientBankExchange)',
};

export default function BankPaymentsPage() {
  // ч.729 (рішення власника): організація і період — з ВЕРХНЬОГО фільтра
  // (шапка застосунку): період реактивно, організація — слідує за шапкою,
  // доки не обрано вручну на сторінці (канон HrOrdersPage).
  const dp = useDefaultPeriod();
  const [orgId, setOrgId] = useOrgFilter();
  // Поля дат на сторінці: слідують за періодом шапки; ручна правка діє до
  // наступної зміни періоду в шапці (ч.730, зауваження власника: поля лишити).
  const [ovrFrom, setOvrFrom] = useState<string | null>(null);
  const [ovrTo, setOvrTo] = useState<string | null>(null);
  useEffect(() => { setOvrFrom(null); setOvrTo(null); }, [dp.dateFrom, dp.dateTo]);
  const dateFromDay = ovrFrom ?? (dp.dateFrom ? dp.dateFrom.slice(0, 10) : '');
  const dateToDay = ovrTo ?? (dp.dateTo ? dp.dateTo.slice(0, 10) : '');
  const dateFrom = dateFromDay ? `${dateFromDay}T00:00:00` : undefined;
  const dateTo = dateToDay ? `${dateToDay}T23:59:59` : undefined;
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const qc = useQueryClient();
  // ч.785: /bank-payments?tab=saved — посилання «дивитись» з виплати/перерахування податків.
  const [searchParams, setSearchParams] = useSearchParams();
  const [tab, setTab] = useState<Tab>(() => {
    const t0 = searchParams.get('tab');
    if (searchParams.get('review') === '1') return 'review';
    return TABS.includes(t0 as Tab) ? (t0 as Tab) : 'paid';
  });
  const [contractorId, setContractorId] = useState('');
  // ч.798: фільтри «Рахунок» і «Валюта».
  const [accountId, setAccountId] = useState('');
  const [currencyCode, setCurrencyCode] = useState('');
  const accountsQ = useQuery({ queryKey: ['bank-accounts', orgId], queryFn: () => refs.bankAccounts(orgId || undefined) });
  const currenciesQ = useQuery({ queryKey: ['currencies'], queryFn: refs.currencies, staleTime: 3_600_000 });
  const accCurrency = useMemo(() => {
    const m = new Map<string, string>();
    (accountsQ.data ?? []).forEach((b) => m.set(b.id, (currenciesQ.data ?? []).find((c) => c.id === b.currency_id)?.code ?? ''));
    return m;
  }, [accountsQ.data, currenciesQ.data]);
  const currencyOptions = useMemo(() => Array.from(new Set(Array.from(accCurrency.values()).filter(Boolean))).sort(), [accCurrency]);
  // 2026-08-04 (рішення власника): контроль вхідних ПН по АВАНСАХ — ПН
  // першої події-оплати привʼязується до платежу (як приходи, ч.289).
  const [pnFilter, setPnFilter] = useState<'' | 'with' | 'without'>('');
  const orgListQ = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  const showPnCol = useMemo(
    // ч.935 (рішення власника): колонка ПН лише для платника ПДВ — обраної
    // організації; без фільтра — якщо хоч одна організація платник.
    () => {
      const list = orgListQ.data?.items ?? [];
      if (orgId) return everVatPayer(list.find((o) => o.id === orgId));
      return list.some((o) => everVatPayer(o));
    },
    [orgListQ.data, orgId]);

  // Спільні параметри списку/підсумку: «збережені» — чернетки за будь-яку
  // дату (вони чекають на виписку); період шапки фільтрує лише сплачені.
  const listParams = useMemo(() => ({
    organization_id: orgId || undefined,
    contractor_id: contractorId || undefined,
    bank_account_id: accountId || undefined,
    currency_code: currencyCode || undefined,
    date_from: tab === 'paid' ? dateFrom : undefined,
    date_to: tab === 'paid' ? dateTo : undefined,
    posted: tab === 'paid',
    pn_link: tab === 'paid' ? (pnFilter || undefined) : undefined,
    needs_review: tab === 'saved' || tab === 'sent' ? false : undefined,
    sent: tab === 'saved' ? false : tab === 'sent' ? true : undefined,
  }), [orgId, contractorId, tab, dateFrom, dateTo, pnFilter, accountId, currencyCode]);
  // ч.833: лічильники вкладок.
  const countsQ = useQuery({
    queryKey: ['bank-payments-tab-counts', orgId],
    queryFn: () => bankPayments.tabCounts(orgId || undefined),
  });
  const cnt = (n?: number) => (n == null ? '' : ` (${n})`);

  const { datasource } = useInfiniteGridDatasource<BankPaymentListItem>(
    (limit, offset, sort) => bankPayments.list({
      ...listParams, sort_by: sort?.sort_by, sort_dir: sort?.sort_dir, limit, offset,
    }),
    [listParams],
    { attachmentsType: 'doc.bank_payments' },
  );
  const sc = useSignControlInfinite<BankPaymentListItem>('doc.bank_payments', orgId);
  const refreshRef = useRef<(() => void) | null>(null);
  const apiRef = useRef<GridApi<BankPaymentListItem> | null>(null);
  const summaryQ = useQuery({
    queryKey: ['bank-payments-summary', listParams],
    queryFn: () => bankPayments.summary(listParams),
  });
  const refreshAll = () => {
    refreshRef.current?.();
    qc.invalidateQueries({ queryKey: ['bank-payments-summary'] });
    qc.invalidateQueries({ queryKey: ['bank-payments-tab-counts'] });
  };
  // ч.929: «оновити при відкритті» — виписка через API-підключення, якщо давня.
  useBankApiRefreshOnOpen(orgId || undefined, () => refreshRef.current?.());

  const cQ = useQuery({
    queryKey: ['contractors-all'],
    queryFn: () => contractorsApi.listAll(),
  });
  const cName = useMemo(() => {
    const m = new Map<string, string>();
    cQ.data?.items.forEach((c) => m.set(c.id, c.name));
    return m;
  }, [cQ.data]);
  const orgName = useMemo(() => {
    const m = new Map<string, string>();
    orgListQ.data?.items.forEach((o) => m.set(o.id, o.name));
    return m;
  }, [orgListQ.data]);

  // ч.727: мультивибір збережених → реєстр у клієнт-банк (лише csv).
  // ч.1184 (запит власника): галочки — на ВСІХ вкладках, сума позначених над таблицею
  // (як у «Розрахунках за документами»); у «збережених» вибір, як і раніше, ще й для
  // вивантаження в банк.
  const [selectedRows, setSelectedRows] = useState<BankPaymentListItem[]>([]);
  const [exportNote, setExportNote] = useState<string | null>(null);
  const selectedOrgIds = new Set(selectedRows.map((r) => r.organization_id));
  const selTotal = sumMoney(selectedRows.map((r) => r.amount));
  const clearSelection = () => { apiRef.current?.deselectAll(); setSelectedRows([]); };
  // Інші фільтри / період — інший перелік: позначки скидаємо (ag-Grid з getRowId
  // тримає вибір між перезавантаженнями — у вивантаження пішли б невидимі рядки).
  useEffect(() => {
    apiRef.current?.deselectAll();
    setSelectedRows((prev) => (prev.length ? [] : prev));
  }, [listParams]);
  const exportMut = useMutation({
    mutationFn: () => bankPayments.exportBank([...selectedOrgIds][0] ?? '', selectedRows.map((r) => r.id)),
    onSuccess: ({ blob, filename, missingIban, bankFormat }) => {
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = filename; a.click();
      URL.revokeObjectURL(url);
      const hint = BANK_HINT[bankFormat] ?? '';
      setExportNote(
        `Файл ${filename} сформовано (${selectedRows.length} шт.).${hint ? ` Завантажте його: ${hint}.` : ''}`
        + (missingIban.length
          ? ` ⚠ Без IBAN отримувача (додайте рахунок контрагенту): ${missingIban.join(', ')}`
          : ''),
      );
      apiRef.current?.deselectAll();
      setSelectedRows([]);
      refreshAll();
    },
    onError: (e: unknown) => {
      const detail = (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      setExportNote(`Не вдалося сформувати файл: ${detail ?? String(e)}`);
    },
  });

  // ч.739: відправити збережені списання у банк через API (Приват/ПУМБ за МФО
  // рахунку) — підпис у кабінеті банку; потрібне підключення (/bank-api).
  const apiConnQ = useQuery({
    queryKey: ['bank-api-connections', orgId],
    queryFn: () => bankApi.list(orgId), enabled: !!orgId && tab === 'saved',
  });
  const hasApi = (apiConnQ.data ?? []).some((c) => c.enabled);
  const sendApi = useMutation({
    mutationFn: () => bankApi.sendPayments([...selectedOrgIds][0] ?? '', selectedRows.map((r) => r.id)),
    onSuccess: (res) => {
      const bad = res.results.filter((x) => !x.ok);
      setExportNote(`Відправлено через API: ${res.sent} з ${res.results.length}. Підпишіть платежі в кабінеті банку.`
        + (bad.length ? ` Не відправлено: ${bad.map((b) => `№${b.number} — ${b.message}`).join('; ')}` : ''));
      apiRef.current?.deselectAll();
      setSelectedRows([]);
      refreshAll();
    },
    onError: (e: unknown) => {
      const detail = (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      setExportNote(`Не вдалося відправити через API: ${detail ?? String(e)}`);
    },
  });

  // Ручна привʼязка ПН до платежу-авансу (авто-матчинг за сумою міг не знайти).
  const [linkFor, setLinkFor] = useState<BankPaymentListItem | null>(null);
  const unlinkedPnQ = useQuery({
    queryKey: ['unlinked-pns', linkFor?.contractor_id, linkFor?.organization_id],
    queryFn: () => taxInvoicesIncoming.list({
      organization_id: linkFor!.organization_id,
      contractor_id: linkFor!.contractor_id!, limit: 100,
    }),
    enabled: !!linkFor?.contractor_id,
  });
  const bind = useMutation({
    mutationFn: (pnId: string) => taxInvoicesIncoming.update(pnId, {
      base_document_type: 'doc.bank_payments',
      base_document_id: linkFor!.id,
    }),
    onSuccess: () => {
      setLinkFor(null);
      refreshAll();
      qc.invalidateQueries({ queryKey: ['tax-invoices-incoming'] });
    },
  });

  const cols = useMemo<ColDef<BankPaymentListItem>[]>(() => {
    const base: ColDef<BankPaymentListItem>[] = [];
    if (tab !== 'saved') {
      // ч.1184: галочка для підрахунку суми позначених (усі рядки).
      base.push({
        colId: '_sel', headerName: '', width: 40,
        headerTooltip: 'Позначте документи — над таблицею зʼявиться їх сума',
        sortable: false, resizable: false, suppressMovable: true,
        checkboxSelection: true,
      });
    }
    if (tab === 'saved') {
      base.push({
        colId: '_sel', headerName: '', headerTooltip: 'Вибір для вивантаження в банк і підрахунку суми',
        width: 40, sortable: false, suppressMovable: true, resizable: false,
        // ч.766: зарплатна платіжка без привʼязки до платіжок податків — не відбирається
        // (жовтий бейдж замість галки; відкрийте платіжку і заповніть ПДФО/ВЗ/ЄСВ).
        checkboxSelection: (p) => !p.data?.operation_type_tax_links_missing,
        cellRenderer: (p: { data?: BankPaymentListItem }) => p.data?.operation_type_tax_links_missing ? (
          <span style={{ color: 'var(--badge-amber-fg)', fontWeight: 700 }}
            title="Виплата ЗП без привʼязки до платіжок ПДФО/ВЗ/ЄСВ — відкрийте платіжку і оберіть ПІ податків, інакше банк не пропустить">!</span>
        ) : null,
      });
    }
    base.push(
      {
        headerName: '', width: 44, sortable: false,
        cellRenderer: (p: { data?: BankPaymentListItem }) =>
          <ImportedBadge source={p.data?.external_source} />,
      },
      {
        field: 'document_number', headerName: t('bd.number'), width: 160,
        cellRenderer: (p: { data?: BankPaymentListItem }) => p.data ? (
          <span>
            {p.data.document_number}
            {p.data.needs_review && (
              <span style={{ color: 'var(--badge-amber-fg)', marginLeft: 4 }}
                title="Створено автоматично з виписки банку: контрагента/операцію не розпізнано — перевірте і проведіть">⚠</span>
            )}
            {p.data.bank_api_ref && !p.data.posted && (
              <span style={{ color: 'var(--badge-blue-fg)', marginLeft: 4, fontSize: '0.75rem' }}
                title={`Відправлено в банк через API (ref ${p.data.bank_api_ref}) — очікує підпису/оплати`}>API</span>
            )}
          </span>
        ) : null,
      },
      {
        field: 'document_date', headerName: t('bd.date'), width: 130,
        valueFormatter: (p) => p.value ? new Date(p.value as string).toLocaleDateString(i18n.language) : '',
      },
      { field: 'bank_account_name', headerName: 'Рахунок', width: 170, sortable: false },
      { field: 'currency_code', headerName: 'Валюта', width: 80, sortable: false },
    );
    if (!orgId) {
      base.push({
        colId: 'organization', headerName: 'Організація', width: 170, sortable: false,
        valueGetter: (p) => p.data ? (orgName.get(p.data.organization_id) ?? '') : '',
      });
    }
    base.push(
      {
        colId: 'contractor', headerName: t('bp.recipient'), width: 240, sortable: false,
        valueGetter: (p) => p.data
          ? (p.data.recipient_display || (p.data.contractor_id ? (cName.get(p.data.contractor_id) ?? '') : ''))
          : '',
      },
      { field: 'payment_purpose', headerName: t('bd.purpose'), flex: 1, minWidth: 220 },
      {
        field: 'amount', headerName: t('bd.amount'), width: 140,
        cellStyle: { textAlign: 'right' },
        valueFormatter: (p) => fmt(p.value as string),
      },
      {
        field: 'amount_currency', headerName: 'У валюті', width: 140, sortable: false,
        cellStyle: { textAlign: 'right' },
        cellRenderer: (p: { data?: BankPaymentListItem }) => p.data?.amount_currency && p.data.currency_code && p.data.currency_code !== 'UAH'
          ? <span title={`Курс ${p.data.exchange_rate ?? '—'}; у грн: ${fmt(p.data.amount)}`}>{fmt(p.data.amount_currency)} {p.data.currency_code}</span>
          : '',
      },
    );
    if (tab === 'paid' && showPnCol) {
      base.push({
        colId: 'pn', headerName: 'ПН', width: 150, sortable: false,
        cellRenderer: (p: { data?: BankPaymentListItem }) => {
          const r = p.data;
          if (!r) return null;
          // ПН очікується лише по оплатах постачальнику-платнику ПДВ.
          if (r.operation_type !== 'TO_SUPPLIER' || !r.contractor_is_vat_payer) {
            return <span style={{ color: '#94a3b8' }}
              title="ПН не очікується (не оплата постачальнику-платнику ПДВ)">—</span>;
          }
          if (r.pn_id) {
            const regDate = r.pn_registration_date
              ? ` ${new Date(r.pn_registration_date).toLocaleDateString('uk-UA')}` : '';
            const isErpn = r.pn_external_source === 'erpn';
            return (
              <a style={{ cursor: 'pointer', color: 'var(--badge-green-fg)', fontWeight: isErpn ? 600 : 400 }}
                title={isErpn
                  ? `ПН №${r.pn_number} — підтверджено випискою ЄРПН${regDate ? `, реєстрація${regDate}` : ''}. Відкрити ПН`
                  : `ПН №${r.pn_number} — створена вручну${r.pn_is_registered ? ', зареєстрована зі слів постачальника' : ', НЕ зареєстрована'}. Відкрити ПН`}
                onClick={(e) => { e.stopPropagation(); navigate(`/tax-invoices-incoming/${r.pn_id}`); }}>
                {isErpn ? `✓ ЄРПН${regDate}` : `✓ №${r.pn_number}`}
              </a>
            );
          }
          // Мʼякше за приходи (амбер, не червоний): ПН на оплату очікується
          // ЛИШЕ якщо це аванс (перша подія). Оплата ПІСЛЯ приходу — ПН
          // привʼязана до приходу, тут її легітимно немає.
          return (
            <span style={{ whiteSpace: 'nowrap' }}>
              <span style={{ color: 'var(--badge-amber-fg)', fontWeight: 600 }}
                title="ПН до оплати не привʼязана. Якщо це АВАНС (перша подія) — постачальник мав скласти ПН на аванс: потягніть з ЄРПН (Кабінет ДПС → Отримати ПН) або привʼяжіть вручну. Якщо оплата ПІСЛЯ приходу — ПН привʼязана до приходу, тут все гаразд.">
                ⚠ немає
              </span>
              {' '}
              <a style={{ cursor: 'pointer', color: 'var(--primary)', fontSize: '0.82rem' }}
                title="Привʼязати наявну вхідну ПН цього постачальника"
                onClick={(e) => { e.stopPropagation(); setLinkFor(r); }}>
                привʼязати
              </a>
            </span>
          );
        },
      });
    }
    if (tab === 'saved') {
      base.push({
        field: 'sent_to_bank_at', headerName: 'Статус', width: 200,
        cellRenderer: (p: { data?: BankPaymentListItem }) => {
          const r = p.data;
          if (!r) return null;
          // ч.794: файл ≠ відправка. csv лише вивантажено (юзер сам імпортує у кабінет
          // банку), через API — справді відправлено (bank_api_ref).
          if (!r.sent_to_bank_at) {
            return <span style={{ color: 'var(--text-secondary)' }} title="Збережено, ще не вивантажено у банк">чернетка</span>;
          }
          const d = new Date(r.sent_to_bank_at).toLocaleDateString('uk-UA');
          return r.bank_api_ref
            ? <span style={{ color: 'var(--badge-blue-fg)' }}
                title="Відправлено у банк через API. Після оплати банком документ підтвердиться випискою/синхронізацією — стане «сплачений»">
                відправлено в банк {d}
              </span>
            : <span style={{ color: 'var(--badge-amber-fg)' }}
                title="Сформовано файл для клієнт-банку (csv) — у банк ще НЕ відправлено: імпортуйте файл у кабінеті банку. Після оплати підтвердіть випискою (Банк → Імпорт виписки)">
                вивантажено у файл {d}
              </span>;
        },
      });
    }
    base.push({
      field: 'posted', headerName: t('bd.posted'), width: 130,
      valueFormatter: (p) => (p.value ? '✓ ' + t('mo.posted_yes') : t('mo.posted_no')),
    });
    return base;
  }, [t, i18n.language, cName, orgName, orgId, showPnCol, navigate, tab]);

  const switchTab = (id: Tab) => {
    setTab(id); setSelectedRows([]); setExportNote(null);
    // ч.794: вкладка — в URL, щоб «назад»/«до списку» з платіжки повертали на неї.
    setSearchParams((sp) => { const n = new URLSearchParams(sp); n.delete('review'); if (id !== 'paid') n.set('tab', id); else n.delete('tab'); return n; }, { replace: true });
  };
  const nowrap = { whiteSpace: 'nowrap' as const, flex: '0 0 auto' };
  const summary = summaryQ.data;

  return (
    <div className="card">
      <div className="toolbar" style={{ gap: '0.4rem' }}>
        <h2 style={{ margin: 0 }}>{t('bp.title')}</h2>
        <button className="primary" style={{ marginLeft: 'auto' }}
          onClick={() => navigate('/bank-payments/new')}>
          + {t('bp.new')}
        </button>
      </div>
      {/* ч.729: усі фільтри в ОДНОМУ рядку без переносу. Організація/період —
          з верхнього фільтра шапки (поля дат на сторінці слідують за нею). */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', flexWrap: 'wrap',
        marginBottom: '0.5rem', minWidth: 0 }}>
        <select value={orgId} style={{ width: 170, ...nowrap }}
          title="Організація: слідує за верхнім фільтром шапки, доки не обрано тут вручну"
          onChange={(e) => { setOrgId(e.target.value); setSelectedRows([]); }}>
          <option value="">Усі організації</option>
          {(orgListQ.data?.items ?? []).map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
        </select>
        {tab === 'paid' && (
          <>
            <div style={{ width: 108, ...nowrap }}>
              <DatePopover value={dateFromDay} onChange={(v) => setOvrFrom(v)} />
            </div>
            <span style={{ color: 'var(--muted)' }}>—</span>
            <div style={{ width: 108, ...nowrap }}>
              <DatePopover value={dateToDay} onChange={(v) => setOvrTo(v)} />
            </div>
          </>
        )}
        <div style={{ width: 190, ...nowrap }}>
          <SearchSelect
            items={(cQ.data?.items ?? []).map((c) => ({ id: c.id, label: c.name, hint: c.edrpou ?? null }))}
            value={contractorId}
            onChange={(id) => setContractorId(id)}
            placeholder="Отримувач: усі"
          />
        </div>
        <select value={accountId} style={{ width: 160, ...nowrap }} title="Розрахунковий рахунок"
          onChange={(e) => setAccountId(e.target.value)}>
          <option value="">Рахунок: усі</option>
          {(accountsQ.data ?? []).map((b) => <option key={b.id} value={b.id}>{b.account_name ?? b.iban}{accCurrency.get(b.id) ? ` (${accCurrency.get(b.id)})` : ''}</option>)}
        </select>
        <select value={currencyCode} style={{ width: 95, ...nowrap }} title="Валюта рахунку"
          onChange={(e) => setCurrencyCode(e.target.value)}>
          <option value="">Валюта: усі</option>
          {currencyOptions.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        {tab === 'paid' && showPnCol && (
          <select value={pnFilter} style={{ width: 135, ...nowrap }}
            title="Контроль ПН по авансах: «без ПН» — оплати постачальникам-платникам ПДВ без привʼязаної ПН"
            onChange={(e) => setPnFilter(e.target.value as '' | 'with' | 'without')}>
            <option value="">ПН: усі</option>
            <option value="without">без ПН (аванси?)</option>
            <option value="with">з ПН</option>
          </select>
        )}
      </div>
      {/* ч.733: вкладки — окремим рядком нижче фільтрів; на «збережені» поруч —
          вивантаження в банк. ч.734: під вкладками — підсумок з сервера. */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.25rem' }}>
        <div className="seg-tabs" style={{ margin: 0 }}>
          <button type="button" className={tab === 'paid' ? 'active' : ''} onClick={() => switchTab('paid')}>
            сплачені
          </button>
          <button type="button" className={tab === 'review' ? 'active' : ''} onClick={() => switchTab('review')}
            title="Списання з виписки банку, де контрагента / вид операції / статтю ДДС не розпізнано">
            нерозпізнані{cnt(countsQ.data?.review)}
          </button>
          <button type="button" className={tab === 'saved' ? 'active' : ''} onClick={() => switchTab('saved')}
            title="Чернетки, підготовлені до відправки в банк">
            збережені{cnt(countsQ.data?.saved)}
          </button>
          <button type="button" className={tab === 'sent' ? 'active' : ''} onClick={() => switchTab('sent')}
            title="Відправлені в банк (через API або файлом) — очікують виписки">
            відправлені{cnt(countsQ.data?.sent)}
          </button>
        </div>
        {tab === 'saved' && (<>
          <button type="button" className="primary" style={nowrap}
            disabled={selectedRows.length === 0 || selectedOrgIds.size !== 1 || exportMut.isPending}
            title={selectedRows.length === 0
              ? 'Позначте списання, які треба відправити в банк'
              : selectedOrgIds.size !== 1
                ? 'Обрані списання різних організацій — вивантажуйте по одній організації'
                : 'Сформувати реєстр платежів для імпорту в кабінет банку (csv; формат обирається за банком рахунку автоматично)'}
            onClick={() => exportMut.mutate()}>
            {exportMut.isPending ? 'формуємо…' : `вивантажити в банк (${selectedRows.length})`}
          </button>
          {hasApi && (
            <button type="button" style={nowrap}
              disabled={selectedRows.length === 0 || selectedOrgIds.size !== 1 || sendApi.isPending}
              title="Відправити обрані списання у банк через API (без файлу); підписати — в кабінеті банку"
              onClick={() => sendApi.mutate()}>
              {sendApi.isPending ? 'надсилаємо…' : `надіслати через API (${selectedRows.length})`}
            </button>
          )}
        </>)}
      </div>
      {exportNote && (
        <div style={{
          marginBottom: '0.5rem', padding: '0.5rem 0.75rem', borderRadius: 6, fontSize: '0.85rem',
          background: exportNote.startsWith('Не вдалося') ? '#fee2e2' : '#dbeafe',
          border: `1px solid ${exportNote.startsWith('Не вдалося') ? '#fca5a5' : '#93c5fd'}`,
        }}>
          {exportNote}
          <button type="button" style={{ marginLeft: 8, padding: '0 0.4rem' }} onClick={() => setExportNote(null)}>×</button>
        </div>
      )}
      {tab === 'review' ? (
        <BankImportPage mode="review" direction="OUT" orgId={orgId} onApplied={refreshAll} />
      ) : (
      <InfiniteDocGrid<BankPaymentListItem>
        copyRow={(r) => navigate(`/bank-payments/new?copy=${r.id}`)}
        deleteRow={{ remove: async (id) => { await bankPayments.remove(id); qc.invalidateQueries({ queryKey: ['bank-payments-tab-counts'] }); } }}
        key={tab}
        headerLeft={(
          // ч.794: у рядку з шестерінкою, ЗЛІВА — «позначити усі · зняти» (збережені) + підсумок.
          <span style={{ color: 'var(--muted)', fontSize: '0.85rem', display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
            {tab === 'saved' && (<>
              <a style={{ cursor: 'pointer' }} title="Позначити всі завантажені рядки (крім тих, де бракує привʼязки податкових платіжок)"
                onClick={() => apiRef.current?.forEachNode((n) => { if (n.data && !n.data.operation_type_tax_links_missing) n.setSelected(true); })}>
                позначити усі
              </a>
              <span>·</span>
            </>)}
            <span>
              {summary
                ? <>{summary.count} док. · {fmt(summary.sum_amount)} грн</>
                : summaryQ.isLoading ? 'рахуємо…' : ''}
            </span>
            <SelectionSum count={selectedRows.length} sum={selTotal} onClear={clearSelection} />
          </span>
        )}
        datasource={sc.wrap(datasource)}
        columnDefs={[...cols, ...sc.column]}
        storageKey={`bank-payments-${tab}`}
        onRowDoubleClicked={(r) => navigate(`/bank-payments/${r.id}${tab !== 'paid' ? `?from=${tab}` : ''}`)}
        getRowStyle={(p) => p.data?.deletion_mark ? { opacity: 0.4 } : sc.getRowStyle(p)}
        refreshRef={refreshRef}
        rowSelection="multiple"
        onSelectionChanged={setSelectedRows}
        getRowId={(r) => r.id}
        apiRef={apiRef}
        isRowSelectable={tab === 'saved' ? (r) => !r.operation_type_tax_links_missing : undefined}
      />
      )}

      {linkFor && (
        <div style={{
          position: 'fixed', inset: 0, background: 'var(--overlay)', zIndex: 60,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }} onClick={() => setLinkFor(null)}>
          <div style={{
            background: 'var(--bg-panel)', borderRadius: 10, padding: '1rem',
            width: 640, maxHeight: '75vh', overflow: 'auto',
          }} onClick={(e) => e.stopPropagation()}>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
              <h3 style={{ margin: 0 }}>
                Привʼязати ПН до оплати №{linkFor.document_number} ({fmt(linkFor.amount)} грн)
              </h3>
              <button style={{ marginLeft: 'auto' }} onClick={() => setLinkFor(null)}>закрити</button>
            </div>
            {unlinkedPnQ.isLoading && <div>Завантаження…</div>}
            {(() => {
              const free = (unlinkedPnQ.data?.items ?? [])
                .filter((p) => !p.base_document_id && !p.deletion_mark);
              if (unlinkedPnQ.isLoading) return null;
              if (!free.length) {
                return (
                  <div style={{ color: 'var(--text-secondary)' }}>
                    Непривʼязаних ПН цього постачальника немає. Потягніть ПН з
                    ЄРПН: Кабінет ДПС → «Отримати ПН».
                  </div>
                );
              }
              return (
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
                  <thead>
                    <tr style={{ background: 'var(--bg-soft-2)', textAlign: 'left' }}>
                      <th style={{ padding: '0.4rem' }}>№ ПН</th>
                      <th style={{ padding: '0.4rem' }}>Дата</th>
                      <th style={{ padding: '0.4rem', textAlign: 'right' }}>Сума</th>
                      <th style={{ padding: '0.4rem' }}>ЄРПН</th>
                      <th style={{ padding: '0.4rem' }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {free.map((p) => (
                      <tr key={p.id} style={{ borderTop: '1px solid var(--border)' }}>
                        <td style={{ padding: '0.4rem' }}>{p.invoice_number}</td>
                        <td style={{ padding: '0.4rem' }}>
                          {new Date(p.invoice_date).toLocaleDateString('uk-UA')}
                        </td>
                        <td style={{ padding: '0.4rem', textAlign: 'right' }}>{fmt(p.total_amount)}</td>
                        <td style={{ padding: '0.4rem' }}>
                          {p.external_source === 'erpn' ? '✓ ЄРПН'
                            : p.is_registered_erpn ? '✓ вручну' : '✗'}
                        </td>
                        <td style={{ padding: '0.4rem' }}>
                          <button disabled={bind.isPending}
                            onClick={() => bind.mutate(p.id)}>обрати</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              );
            })()}
          </div>
        </div>
      )}
    </div>
  );
}
